<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import { useUserDataStorage } from '~/store/userdata'
import MainMenu from './MainMenu.vue'

const userDataStore = useUserDataStorage()
const { user } = storeToRefs(userDataStore)
const route = useRoute()

const scrolled = ref(false)
const productsInCart = computed(() => userDataStore.browserCart?.products.length ?? 0)
const administrationVisible = computed(() => user.value?.role?.name === 'Authenticated')

const navigation = [
  { label: 'Leistungen', to: '/#leistungen' },
  { label: 'Sortiment', to: '/shop' },
  { label: 'Referenzen', to: '/references' },
  { label: 'Über uns', to: '/about' },
  { label: 'FAQ', to: '/faq' },
  { label: 'Blog', to: '/blog' },
]

const companyNavigation = [
  { label: 'Über uns', to: '/about' },
  { label: 'Kontakt', to: '/contact' },
  { label: 'Newsletter', to: '/newsletter' },
]

function isNavigationActive(to: string) {
  if (to === '/#leistungen') return route.path === '/' && route.hash === '#leistungen'
  if (to === '/shop') return route.path.startsWith('/shop') && route.path !== '/shop/request'
  if (to === '/references') return route.path.startsWith('/references')
  if (to === '/blog') return route.path.startsWith('/blog')
  if (to === '/about') return route.path === '/about'
  return route.path === to
}

function handleScroll() {
  scrolled.value = window.scrollY > 24
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll()
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <header class="site-header" :class="{ 'site-header--scrolled': scrolled }">
    <div class="contact-bar">
      <div class="contact-bar__inner">
        <a href="tel:+436642247433"><i class="pi pi-phone"></i><span>Anrufen</span></a>
        <a href="mailto:info@mody.at"><i class="pi pi-envelope"></i><span>E-Mail</span></a>
        <a href="https://wa.me/436642247433" rel="noopener noreferrer" target="_blank"><i class="pi pi-whatsapp"></i><span>WhatsApp</span></a>
      </div>
    </div>

    <div class="navigation-bar">
      <div class="navigation-bar__inner">
        <NuxtLink to="/" class="brand-link" aria-label="Mody Veranstaltungstechnik – Startseite">
          <NuxtImg src="/rgb_negativ.png" alt="Mody Veranstaltungstechnik" class="brand-logo" />
        </NuxtLink>

        <nav class="desktop-navigation" aria-label="Hauptnavigation">
          <div v-for="item in navigation" :key="item.to" class="desktop-navigation__item">
            <NuxtLink
              :to="item.to"
              class="desktop-navigation__link"
              active-class=""
              exact-active-class=""
              :class="{ 'is-active': isNavigationActive(item.to) }"
            >
              {{ item.label }}
              <i v-if="item.to === '/about'" class="pi pi-angle-down" aria-hidden="true"></i>
            </NuxtLink>

            <div v-if="item.to === '/about'" class="desktop-submenu" aria-label="Unternehmen">
              <NuxtLink
                v-for="companyItem in companyNavigation"
                :key="companyItem.to"
                :to="companyItem.to"
                active-class=""
                exact-active-class=""
                :class="{ 'is-current': route.path === companyItem.to }"
              >
                {{ companyItem.label }}
              </NuxtLink>
            </div>
          </div>
        </nav>

        <div class="desktop-actions">
          <NuxtLink v-if="administrationVisible" to="/admin/adminProducts" class="admin-link">Administration</NuxtLink>
          <NuxtLink
            to="/shop/request"
            class="request-link"
            active-class=""
            exact-active-class=""
            :class="{ 'request-link--active': route.path === '/shop/request' }"
          >
            Angebot anfordern
            <span v-if="productsInCart" class="cart-count" :aria-label="`${productsInCart} Produkte ausgewählt`">{{ productsInCart }}</span>
          </NuxtLink>
        </div>

        <MainMenu
          class="mobile-navigation"
          :administration-visible="administrationVisible"
          :cart-count="productsInCart"
        />
      </div>
    </div>
  </header>
</template>

<style scoped lang="scss">
.site-header{
  position: sticky;
  top: 0;
  z-index: 1050;
  width: 100%;
  color: white;
  background: rgba(12, 12, 13, 0.98);
  border-bottom: 1px solid transparent;
  transition: background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
}

.site-header--scrolled{
  background: rgba(12, 12, 13, 0.94);
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.24);
  backdrop-filter: blur(16px);
}

.contact-bar{
  height: 44px;
  background: #09090a;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.contact-bar__inner{
  width: min(100%, 1152px);
  height: 100%;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;

  a{
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    min-width: 44px;
    min-height: 44px;
    padding-inline: 0.35rem;
    color: rgb(214 211 209);
    font-size: 0.82rem;
    line-height: 1;
    text-decoration: none;

    &:hover{
      color: white;
    }
  }
}

.navigation-bar{
  height: 64px;
}

.navigation-bar__inner{
  width: min(100%, 1216px);
  height: 100%;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
}

.brand-link{
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

.brand-logo{
  display: block;
  width: auto;
  height: 48px;
}

.desktop-navigation,
.desktop-actions{
  display: none;
}

.mobile-navigation{
  margin-left: auto;
}

@media (min-width: 1024px){
  .contact-bar{
    height: 36px;
  }

  .contact-bar__inner{
    justify-content: flex-end;
    padding-inline: 2rem;

    a{
      min-height: 36px;
      padding-inline: 0;
    }
  }

  .navigation-bar__inner{
    padding-inline: 2rem;
  }

  .desktop-navigation{
    display: flex;
    align-items: center;
    justify-content: center;
    gap: clamp(1rem, 2vw, 1.8rem);
    margin-left: auto;

    .desktop-navigation__item{
      position: relative;
    }

    .desktop-navigation__link{
      position: relative;
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      color: rgb(214 211 209);
      font-size: 0.94rem;
      font-weight: 550;
      text-decoration: none;
      white-space: nowrap;

      &::after{
        content: '';
        position: absolute;
        right: 0;
        bottom: -9px;
        left: 0;
        height: 2px;
        background: #E39737;
        transform: scaleX(0);
        transition: transform 160ms ease;
      }

      &:hover,
      &:focus-visible,
      &.is-active{
        color: white;
      }

      &:hover::after,
      &:focus-visible::after,
      &.is-active::after{
        transform: scaleX(1);
      }

      i{
        font-size: 0.72rem;
        transition: transform 160ms ease;
      }
    }

    .desktop-navigation__item:hover .desktop-navigation__link i,
    .desktop-navigation__item:focus-within .desktop-navigation__link i{
      transform: rotate(180deg);
    }

    .desktop-submenu{
      position: absolute;
      top: calc(100% + 17px);
      left: 50%;
      width: 190px;
      padding: 0.55rem;
      display: grid;
      gap: 0.2rem;
      background: #171719;
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 0.8rem;
      box-shadow: 0 18px 45px rgba(0, 0, 0, 0.45);
      z-index: 10;
      opacity: 0;
      visibility: hidden;
      transform: translate(-50%, -6px);
      transition: opacity 160ms ease, transform 160ms ease, visibility 160ms ease;

      &::before{
        content: '';
        position: absolute;
        right: 0;
        bottom: 100%;
        left: 0;
        height: 18px;
      }

      a{
        padding: 0.7rem 0.8rem;
        color: rgb(214 211 209);
        border-radius: 0.5rem;
        font-size: 0.9rem;
        text-decoration: none;

        &:hover,
        &:focus-visible,
        &.is-current{
          color: white;
          background: rgba(227, 151, 55, 0.13);
        }

        &:focus-visible{
          outline: 2px solid #E39737;
          outline-offset: 1px;
        }
      }
    }

    .desktop-navigation__item:hover .desktop-submenu,
    .desktop-navigation__item:focus-within .desktop-submenu{
      opacity: 1;
      visibility: visible;
      transform: translate(-50%, 0);
    }
  }

  .desktop-actions{
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .admin-link{
    color: rgb(168 162 158);
    font-size: 0.82rem;
    text-decoration: none;
  }

  .request-link{
    min-height: 42px;
    padding: 0.65rem 1.15rem;
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    color: #111214;
    background: #E39737;
    border-radius: $public-radius-control;
    font-size: 0.9rem;
    font-weight: 700;
    text-decoration: none;
    white-space: nowrap;

    &:hover,
    &:focus-visible{
      background: #efa94d;
    }
  }

  .request-link--active{
    box-shadow: 0 0 0 2px white;
  }

  .cart-count{
    min-width: 1.35rem;
    height: 1.35rem;
    padding-inline: 0.3rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: white;
    background: #111214;
    border-radius: 999px;
    font-size: 0.72rem;
  }

  .mobile-navigation{
    display: none;
  }
}
</style>
